import { useCallback, useContext, useState } from "react";

import { useProductData2 } from "../context/products";
import { CartContext } from "../context/CartProvider";
import { useAddToCartWithLuxe } from "../context/LuxeProvider";
import StoreContext from "../context/store";
import { isOutOfStock } from "@helpers/product";
import { useTracking } from "../context/Tracking";

import {
  useCurrentCountryCode,
  useCurrentCurrencyCode,
  useProductPricing,
} from "../hooks/usePrices";
import useDiscountsInfo from "../hooks/useDiscountsInfo";

import { useALError } from "../helpers/ErrorBoundary/ALErrorBoundary";

import { ComponentType } from "../constants/ComponentType";
import { ALLoading, ALButtonV2 } from "./ALComponents";
import ProductElement from "./ProductElement";
import TAG from "../constants/Tag";
import { Country } from "../constants/Country";
import IconCloseRound from "./icons/svgs/close-delete.svg";

import "./productRecommendationsPopup/styles.scss";

function ProductRecommendationsPopup() {
  const { isSideCartOpen } = useContext(CartContext);
  const { recommendedProductsPopupData, setRecommendedProductsPopupData } =
    useContext(StoreContext);

  const countryCode = useCurrentCountryCode();
  const currencyCode = useCurrentCurrencyCode();
  const discountsInfo = useDiscountsInfo();

  const { trackRecommendedProductsPopupAction, trackRecommendedProductsATC } = useTracking();
  const addToCartWithLuxe = useAddToCartWithLuxe();
  const { throwReport } = useALError();

  const [igProductPrices, setIgProductPrices] = useState({});

  const {
    open = false,
    handles = [],
    title: popupTitle = "",
    recommendationPopupImage = "",
  } = recommendedProductsPopupData || {};
  const { data: products, isInitialLoading: isLoading } = useProductData2(
    handles,
    "long",
    countryCode
  );

  const availableProducts = products.filter((p) => !isOutOfStock(p.shopify, countryCode));

  const { products: productsWithPrices, isInitialLoading: pricesLoading } = useProductPricing({
    products: availableProducts,
  });

  const showAddAllToCart =
    !pricesLoading &&
    productsWithPrices?.length > 0 &&
    productsWithPrices.every((product) => product.shopify.variants?.length === 1);

  const onClosePopup = () => {
    setRecommendedProductsPopupData({
      open: false,
      handles: [],
      title: "",
      recommendationPopupImage: "",
    });
    trackRecommendedProductsPopupAction({ type: "close" });
  };

  const prepProductForATC = useCallback(
    (product) => {
      const igPrices = igProductPrices[product.handle] || {};

      const {
        shopify: { shopifyId, title, productType, images, variants },
        handle,
      } = product;

      return {
        quantity: 1,
        attributes: [],
        variant: {
          id: variants[0].id,
          sku: variants[0].sku,
          price: igPrices.finalPrice,
          compareAtPrice: igPrices.compareAtPrice,
          product: {
            title,
            id: shopifyId,
            handle,
            productType,
            images,
          },
        },
        attributesProperties: {
          isUS: countryCode === Country.US,
          currencyCode,
          prices: igPrices,
          isFinalSale: product.shopify.tags.includes(TAG.FINAL_SALE),
          discountsInfo,
          igPrices,
        },
      };
    },
    [countryCode, currencyCode, discountsInfo, igProductPrices]
  );

  const onAddAllToCart = useCallback(async () => {
    try {
      const dataProductsToAdd = [];
      productsWithPrices.forEach((product) => {
        const dataProductToAdd = prepProductForATC(product);
        dataProductsToAdd.push(dataProductToAdd);
      });
      await addToCartWithLuxe({ dataProductsToAdd });
      trackRecommendedProductsATC({
        type: "add_all_to_cart",
        handles: productsWithPrices.map((p) => p.handle),
      });
    } catch (error) {
      console.error(error);
      throwReport(error);
    }
  }, [
    addToCartWithLuxe,
    productsWithPrices,
    prepProductForATC,
    trackRecommendedProductsATC,
    throwReport,
  ]);

  return (
    <div className="product_recommendations__overlay" style={{ zIndex: isSideCartOpen ? 1 : 9999 }}>
      <div
        onClick={onClosePopup}
        className={`backdrop ${open ? "backdrop-visible" : "backdrop-closed"}`}
      />

      <div className={`product_recommendations__content ${open ? "open" : "closed"}`}>
        {isLoading && <ALLoading />}
        {!isLoading && (
          <>
            <div className="flex justify-between items-center p-[20px] mobile:py-[17px] mobile:px-[15px] border-b border-dark-grey">
              <h3 className="typo-h2 font-bold">{popupTitle}</h3>
              <button type="button" onClick={onClosePopup} data-testid="close-popup">
                <IconCloseRound
                  fill="none"
                  stroke="#000000"
                  width="15px"
                  height="15px"
                  alt="close"
                />
              </button>
            </div>
            <div className="product_recommendations__products">
              {recommendationPopupImage && (
                <img
                  className="w-full flex-shrink-0 mobile:h-[206px] desktop:h-[241.83px] rounded-[6px] object-cover object-center"
                  src={recommendationPopupImage}
                  alt={popupTitle}
                  data-testid="popup-image"
                />
              )}
              {productsWithPrices.length > 0 &&
                productsWithPrices.map((product) => (
                  <ProductElement
                    key={product.id}
                    product={product}
                    pricesLoading={pricesLoading}
                    componentType={ComponentType.PRODUCT_RECOMMENDATIONS}
                    igProductPrices={igProductPrices}
                    setIgProductPrices={setIgProductPrices}
                  />
                ))}
            </div>
            {showAddAllToCart && (
              <div
                data-testid="show-add-all-to-cart"
                className="p-[20px] mobile:p-[15px] mt-auto shadow-[0px_-3px_15px_3px_rgba(0,0,0,0.1)]"
                onClick={onAddAllToCart}
              >
                <ALButtonV2 size="large" variant="primary" fullWidth>
                  Add All to Cart
                </ALButtonV2>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProductRecommendationsPopup;
